import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrgHall } from "@/hooks/useOrgHall";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { sv } from "date-fns/locale";
import { ArrowLeft, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

type PeriodKey = "today" | "week" | "month";

/** Calculate the number of bookable 1-hour slots for a court given hall opening/closing times */
function calcTotalSlots(openingTime: string | null, closingTime: string | null): number {
  const startH = parseInt(openingTime?.split(":")[0] ?? "7", 10);
  let endH = parseInt(closingTime?.split(":")[0] ?? "23", 10);
  if (endH === 0) endH = 24;
  return Math.max(endH - startH, 0);
}

export default function DashboardOccupancy() {
  const { t } = useTranslation();
  const { courts, hall, isLoading: hallLoading } = useOrgHall();
  const [period, setPeriod] = useState<PeriodKey>("today");

  const today = new Date().toISOString().split("T")[0];

  // Compute date range based on period
  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === "today") {
      return { start: today, end: today, days: 1 };
    }
    if (period === "week") {
      const ws = startOfWeek(now, { weekStartsOn: 1 });
      const we = endOfWeek(now, { weekStartsOn: 1 });
      return {
        start: format(ws, "yyyy-MM-dd"),
        end: format(we, "yyyy-MM-dd"),
        days: 7,
      };
    }
    // month
    const ms = startOfMonth(now);
    const me = endOfMonth(now);
    const dayCount = me.getDate();
    return {
      start: format(ms, "yyyy-MM-dd"),
      end: format(me, "yyyy-MM-dd"),
      days: dayCount,
    };
  }, [period, today]);

  // Previous period date range for comparison
  const prevRange = useMemo(() => {
    if (period === "today") {
      const yesterday = format(subDays(new Date(), 1), "yyyy-MM-dd");
      return { start: yesterday, end: yesterday, days: 1 };
    }
    if (period === "week") {
      const prevWeekEnd = subDays(
        startOfWeek(new Date(), { weekStartsOn: 1 }),
        1
      );
      const prevWeekStart = startOfWeek(prevWeekEnd, { weekStartsOn: 1 });
      return {
        start: format(prevWeekStart, "yyyy-MM-dd"),
        end: format(prevWeekEnd, "yyyy-MM-dd"),
        days: 7,
      };
    }
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMs = startOfMonth(prevMonth);
    const prevMe = endOfMonth(prevMonth);
    return {
      start: format(prevMs, "yyyy-MM-dd"),
      end: format(prevMe, "yyyy-MM-dd"),
      days: prevMe.getDate(),
    };
  }, [period]);

  const slotsPerCourtPerDay = calcTotalSlots(
    hall?.opening_time ?? null,
    hall?.closing_time ?? null
  );

  // Current period bookings per court
  const { data: currentBookings = [], isLoading: loadingCurrent } = useQuery({
    queryKey: ["dashboard-occupancy-current", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("court_id, start_time, duration_minutes")
        .neq("status", "cancelled")
        .gte("start_time", dateRange.start + "T00:00:00")
        .lte("start_time", dateRange.end + "T23:59:59");
      if (error) throw error;
      return data ?? [];
    },
    enabled: courts.length > 0,
  });

  // Previous period bookings for comparison
  const { data: prevBookings = [] } = useQuery({
    queryKey: ["dashboard-occupancy-prev", prevRange.start, prevRange.end],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("court_id, start_time, duration_minutes")
        .neq("status", "cancelled")
        .gte("start_time", prevRange.start + "T00:00:00")
        .lte("start_time", prevRange.end + "T23:59:59");
      if (error) throw error;
      return data ?? [];
    },
    enabled: courts.length > 0,
  });

  // Per-court breakdown
  const courtStats = useMemo(() => {
    const totalSlots = slotsPerCourtPerDay * dateRange.days;
    if (totalSlots === 0) return [];

    return courts
      .filter((c) => c.status === "active")
      .map((court) => {
        // Count booked hours (sum of duration_minutes / 60)
        const bookedHours = currentBookings
          .filter((b) => b.court_id === court.id)
          .reduce((sum, b) => sum + (b.duration_minutes ?? 60) / 60, 0);

        const prevHours = prevBookings
          .filter((b) => b.court_id === court.id)
          .reduce((sum, b) => sum + (b.duration_minutes ?? 60) / 60, 0);

        const prevTotalSlots = slotsPerCourtPerDay * prevRange.days;
        const pct = Math.round((bookedHours / totalSlots) * 100);
        const prevPct =
          prevTotalSlots > 0 ? Math.round((prevHours / prevTotalSlots) * 100) : 0;

        return {
          id: court.id,
          name: court.name,
          booked: Math.round(bookedHours),
          total: totalSlots,
          pct: Math.min(pct, 100),
          prevPct,
          trend: pct - prevPct,
        };
      });
  }, [courts, currentBookings, prevBookings, slotsPerCourtPerDay, dateRange.days, prevRange.days]);

  // Overall occupancy
  const overall = useMemo(() => {
    if (courtStats.length === 0) return { pct: 0, booked: 0, total: 0, trend: 0 };
    const booked = courtStats.reduce((s, c) => s + c.booked, 0);
    const total = courtStats.reduce((s, c) => s + c.total, 0);
    const pct = total > 0 ? Math.round((booked / total) * 100) : 0;

    const prevBooked = courtStats.reduce((s, c) => {
      const prevTotal = slotsPerCourtPerDay * prevRange.days;
      return s + Math.round((c.prevPct / 100) * prevTotal);
    }, 0);
    const prevTotal = courtStats.length * slotsPerCourtPerDay * prevRange.days;
    const prevPct = prevTotal > 0 ? Math.round((prevBooked / prevTotal) * 100) : 0;

    return { pct, booked, total, trend: pct - prevPct };
  }, [courtStats, slotsPerCourtPerDay, prevRange.days]);

  // Peak / off-peak breakdown (only for "today")
  const peakStats = useMemo(() => {
    if (period !== "today") return null;

    const startH = parseInt(hall?.opening_time?.split(":")[0] ?? "7", 10);
    let endH = parseInt(hall?.closing_time?.split(":")[0] ?? "23", 10);
    if (endH === 0) endH = 24;
    const courtCount = courts.filter((c) => c.status === "active").length;
    if (courtCount === 0) return null;

    const morningSlots = Math.max(Math.min(12, endH) - startH, 0) * courtCount;
    const afternoonSlots = Math.max(Math.min(17, endH) - Math.max(12, startH), 0) * courtCount;
    const eveningSlots = Math.max(endH - Math.max(17, startH), 0) * courtCount;

    let morningBooked = 0;
    let afternoonBooked = 0;
    let eveningBooked = 0;

    for (const b of currentBookings) {
      const hour = new Date(b.start_time).getHours();
      const hours = (b.duration_minutes ?? 60) / 60;
      if (hour < 12) morningBooked += hours;
      else if (hour < 17) afternoonBooked += hours;
      else eveningBooked += hours;
    }

    return {
      morning: {
        pct: morningSlots > 0 ? Math.round((morningBooked / morningSlots) * 100) : 0,
        label: t("dashboard.morning", "Förmiddag") + " (05–12)",
      },
      afternoon: {
        pct: afternoonSlots > 0 ? Math.round((afternoonBooked / afternoonSlots) * 100) : 0,
        label: t("dashboard.afternoon", "Eftermiddag") + " (12–17)",
      },
      evening: {
        pct: eveningSlots > 0 ? Math.round((eveningBooked / eveningSlots) * 100) : 0,
        label: t("dashboard.evening", "Kväll") + " (17–23)",
      },
    };
  }, [period, currentBookings, courts, hall]);

  const isLoading = hallLoading || loadingCurrent;

  function TrendArrow({ value }: { value: number }) {
    if (value > 0) return <span className="text-green-600 text-xs flex items-center gap-0.5"><TrendingUp className="h-3 w-3" /> +{value}%</span>;
    if (value < 0) return <span className="text-red-600 text-xs flex items-center gap-0.5"><TrendingDown className="h-3 w-3" /> {value}%</span>;
    return <span className="text-muted-foreground text-xs flex items-center gap-0.5"><Minus className="h-3 w-3" /> 0%</span>;
  }

  const periodLabel = useMemo(() => {
    if (period === "today") return format(new Date(), "EEEE d MMMM", { locale: sv });
    if (period === "week") return t("dashboard.thisWeek", "Denna vecka");
    return t("dashboard.thisMonth", "Denna månad");
  }, [period, t]);

  const periodButtons: { key: PeriodKey; label: string }[] = [
    { key: "today", label: t("dashboard.today", "Idag") },
    { key: "week", label: t("dashboard.week", "Vecka") },
    { key: "month", label: t("dashboard.month", "Månad") },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/manager/dashboard"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold">
          {t("dashboard.occupancy", "Beläggning")} — {periodLabel}
        </h1>
      </div>

      {/* Period selector */}
      <div className="flex gap-2">
        {periodButtons.map((p) => (
          <Button
            key={p.key}
            variant={period === p.key ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Overall stat */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold">{overall.pct}%</span>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {overall.booked}/{overall.total}{" "}
                    {t("dashboard.hoursBooked", "timmar bokade")}
                  </p>
                  <TrendArrow value={overall.trend} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Per-court breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("dashboard.perCourt", "Per bana")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {courtStats.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t("dashboard.noCourts", "Inga banor konfigurerade")}
                </p>
              ) : (
                courtStats.map((court) => (
                  <div key={court.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{court.name}</span>
                      <div className="flex items-center gap-2">
                        <span>
                          {court.booked}/{court.total}h — {court.pct}%
                        </span>
                        <TrendArrow value={court.trend} />
                      </div>
                    </div>
                    <Progress value={court.pct} className="h-3" />
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Peak / off-peak (today only) */}
          {peakStats && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("dashboard.peakOffPeak", "Tid på dagen")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {([peakStats.morning, peakStats.afternoon, peakStats.evening] as const).map(
                  (slot) => (
                    <div key={slot.label} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{slot.label}</span>
                        <span>{slot.pct}%</span>
                      </div>
                      <Progress value={slot.pct} className="h-2" />
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
